import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { saveSession } from "@/lib/session";
import "@/enterprise-2.css";
import "@/enterprise-2-apple.css";

const API = process.env.REACT_APP_BACKEND_URL;
const EMERGENT_AUTH = "https://auth.emergentagent.com/";

/**
 * Unified sign-in page.
 * Three routes to a session_token cookie:
 *  1. Continue with Google  (Emergent-managed) → redirects to /auth/callback
 *  2. Email OTP             (6-digit code, default)
 *  3. Email Magic Link      (single-click link)
 *
 * All three converge on the leads collection and set the same session_token.
 */
export default function Login2() {
  const nav = useNavigate();
  const loc = useLocation();

  const returnTo = useMemo(() => {
    const p = new URLSearchParams(loc.search).get("return_to");
    return p && p.startsWith("/") ? p : "/gateway-2";
  }, [loc.search]);

  const [mode, setMode] = useState("otp"); // 'otp' | 'magic'
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState("enter"); // 'enter' | 'sent' | 'verified'
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    if (phase === "sent" && mode === "otp" && codeRef.current) {
      codeRef.current.focus();
    }
  }, [phase, mode]);

  const goGoogle = () => {
    const origin = window.location.origin;
    const cb = `${origin}/auth/callback?return_to=${encodeURIComponent(returnTo)}`;
    window.location.href = `${EMERGENT_AUTH}?redirect=${encodeURIComponent(cb)}`;
  };

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !email.includes("@")) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      if (mode === "otp") {
        await axios.post(`${API}/api/auth/email/request-otp`, { email: email.trim() });
      } else {
        await axios.post(`${API}/api/auth/email/request-magic-link`, {
          email: email.trim(),
          return_to: returnTo,
        });
      }
      setStatus("idle");
      setPhase("sent");
      setResendIn(30);
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail || "Could not send that. Please try again."
      );
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (code.trim().length !== 6) {
      setStatus("error");
      setErrorMsg("Enter the 6-digit code we emailed you.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(
        `${API}/api/auth/email/verify-otp`,
        { email: email.trim(), code: code.trim() },
        { withCredentials: true }
      );
      if (res.data?.ok) {
        saveSession(res.data.session_token);
        setPhase("verified");
        setTimeout(() => nav(returnTo, { replace: true }), 400);
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Verification failed.");
    }
  };

  return (
    <div data-testid="login-2-page" className="login-2" style={styles.wrap}>
      <div style={styles.lockup}>
        <Link to="/gateway-2" style={styles.lockupLink} aria-label="Crowther">
          <img src="/brand/gateway/crowther-full-forest.webp" alt="Crowther" style={{ height: 22, width: "auto", display: "block", filter: "brightness(0.12) saturate(0)" }} />
        </Link>
      </div>

      <main style={styles.centre}>
        <p style={styles.eyebrow}>Sign in</p>
        <h1 style={styles.title}>
          Continue with your <em style={styles.em}>Crowther account</em>.
        </h1>
        <p style={styles.sub}>
          One session, whether you use Google or email. First-time sign-ins are
          welcome.
        </p>

        <div style={styles.card}>
          <button
            type="button"
            onClick={goGoogle}
            style={styles.googleBtn}
            data-testid="login-google-btn"
          >
            <GoogleGlyph />
            <span>Continue with Google</span>
          </button>

          <div style={styles.divider}>
            <span style={styles.dividerLine} />
            <span style={styles.dividerText}>or use email</span>
            <span style={styles.dividerLine} />
          </div>

          {phase === "enter" && (
            <>
              <div style={styles.tabs} data-testid="login-mode-tabs">
                <TabBtn active={mode === "otp"} onClick={() => setMode("otp")} testid="login-tab-otp">
                  6-digit code
                </TabBtn>
                <TabBtn active={mode === "magic"} onClick={() => setMode("magic")} testid="login-tab-magic">
                  Magic link
                </TabBtn>
              </div>

              <form onSubmit={requestOtp} style={styles.form}>
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  data-testid="login-email"
                  autoComplete="email"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  style={{
                    ...styles.primaryBtn,
                    opacity: status === "loading" ? 0.6 : 1,
                    cursor: status === "loading" ? "wait" : "pointer",
                  }}
                  data-testid="login-email-submit"
                >
                  {status === "loading"
                    ? "Sending…"
                    : mode === "otp"
                    ? "Email me a code →"
                    : "Email me a link →"}
                </button>
              </form>
            </>
          )}

          {phase === "sent" && mode === "otp" && (
            <form onSubmit={verifyOtp} style={styles.form} data-testid="login-otp-form">
              <p style={styles.note}>
                We sent a 6-digit code to <strong>{email}</strong>. It expires
                in 10 minutes.
              </p>
              <input
                ref={codeRef}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                placeholder="— — — — — —"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                style={{ ...styles.input, letterSpacing: "0.3em", textAlign: "center", fontSize: 20 }}
                data-testid="login-otp-code"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                style={{
                  ...styles.primaryBtn,
                  opacity: status === "loading" ? 0.6 : 1,
                  cursor: status === "loading" ? "wait" : "pointer",
                }}
                data-testid="login-otp-verify"
              >
                {status === "loading" ? "Verifying…" : "Verify code →"}
              </button>
              <div style={styles.resendRow}>
                <button
                  type="button"
                  onClick={() => {
                    setPhase("enter");
                    setCode("");
                    setStatus("idle");
                    setErrorMsg("");
                  }}
                  style={styles.linkBtn}
                  data-testid="login-otp-change-email"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  disabled={resendIn > 0}
                  onClick={requestOtp}
                  style={{ ...styles.linkBtn, opacity: resendIn > 0 ? 0.5 : 1 }}
                  data-testid="login-otp-resend"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          {phase === "sent" && mode === "magic" && (
            <div style={styles.form} data-testid="login-magic-sent">
              <p style={styles.note}>
                We emailed a sign-in link to <strong>{email}</strong>. Open it on
                this device to complete sign-in. The link expires in 15 minutes.
              </p>
              <button
                type="button"
                onClick={() => {
                  setPhase("enter");
                  setStatus("idle");
                  setErrorMsg("");
                }}
                style={styles.secondaryBtn}
                data-testid="login-magic-back"
              >
                Use a different email
              </button>
            </div>
          )}

          {phase === "verified" && (
            <div style={styles.form} data-testid="login-verified">
              <p style={styles.note}>Signed in. Redirecting…</p>
            </div>
          )}

          {status === "error" && errorMsg && (
            <div style={styles.err} data-testid="login-error">
              {errorMsg}
            </div>
          )}
        </div>

        <p style={styles.legal}>
          By continuing you agree to Crowther's{" "}
          <Link to="/terms" style={styles.legalLink}>Terms</Link> and{" "}
          <Link to="/privacy" style={styles.legalLink}>Privacy Policy</Link>.
        </p>
      </main>

      <footer style={styles.footer}>
        <span>© {new Date().getFullYear()} Crowther</span>
        <span style={{ opacity: 0.6 }}>
          <a href="mailto:hello@crowtherlabs.com" style={styles.footLink}>
            hello@crowtherlabs.com
          </a>
        </span>
      </footer>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        ...styles.tabBtn,
        background: active ? "rgba(106,161,163,0.16)" : "transparent",
        color: active ? "#21405A" : "rgba(26,30,29,0.6)",
        borderColor: active ? "rgba(106,161,163,0.6)" : "rgba(26,30,29,0.14)",
      }}
    >
      {children}
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.71-1.58 2.68-3.9 2.68-6.61z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.19l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.93v2.33A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.97 10.7A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.17.29-1.7V4.97H.93A9 9 0 0 0 0 9c0 1.45.35 2.83.93 4.03l3.04-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.42 0 9 0A9 9 0 0 0 .93 4.97L3.97 7.3C4.68 5.16 6.66 3.58 9 3.58z"/>
    </svg>
  );
}

const styles = {
  wrap: {
    minHeight: "100vh",
    background: "#F5F5F7",
    color: "#1A1E1D",
    fontFamily: "var(--font-sans)",
    display: "flex",
    flexDirection: "column",
    padding: "40px 48px",
    boxSizing: "border-box",
  },
  lockup: { display: "inline-flex", alignItems: "center", gap: 10 },
  lockupLink: { textDecoration: "none", color: "inherit" },
  brand: {
    fontSize: 18, fontWeight: 500, letterSpacing: "-0.005em", color: "#1A1E1D",
  },
  centre: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    maxWidth: 520,
    margin: "0 auto",
    padding: "0 16px",
    width: "100%",
  },
  eyebrow: {
    fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
    color: "#21405A", marginBottom: 22, fontWeight: 500, letterSpacing: "0.02em", textTransform: "none", fontSize: 13,
  },
  title: {
    fontFamily: "var(--font-sans)",
    fontSize: "clamp(34px, 4.4vw, 56px)",
    lineHeight: 1.02,
    letterSpacing: "-0.035em",
    fontWeight: 400,
    color: "#1A1E1D",
    margin: "0 0 16px",
    maxWidth: "18ch",
  },
  em: { fontStyle: "normal", color: "#21405A" },
  sub: {
    fontSize: 15, lineHeight: 1.6, color: "rgba(26,30,29,0.66)",
    marginBottom: 32, maxWidth: 460,
  },
  card: {
    width: "100%",
    background: "#FFFFFF",
    border: "1px solid rgba(26,30,29,0.09)",
    borderRadius: 28,
    padding: 32,
    boxShadow: "0 1px 2px rgba(26,30,29,0.04), 0 14px 40px -22px rgba(26,30,29,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    boxSizing: "border-box",
  },
  googleBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    gap: 10,
    padding: "14px 20px",
    fontSize: 14, fontFamily: "var(--font-sans)", fontWeight: 500,
    letterSpacing: "0.02em",
    background: "#1A1E1D", color: "#FFFFFF",
    border: 0, borderRadius: 999, cursor: "pointer",
    transition: "background 200ms, transform 200ms",
  },
  divider: {
    display: "flex", alignItems: "center", gap: 12,
  },
  dividerLine: {
    flex: 1, height: 1, background: "rgba(26,30,29,0.1)",
  },
  dividerText: {
    fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
    color: "rgba(26,30,29,0.5)",
  },
  tabs: {
    display: "flex", gap: 8,
  },
  tabBtn: {
    flex: 1,
    padding: "10px 14px",
    fontSize: 12, fontFamily: "var(--font-sans)",
    letterSpacing: "0.04em",
    border: "1px solid",
    borderRadius: 999,
    cursor: "pointer",
    transition: "background 180ms, color 180ms, border-color 180ms",
  },
  form: { display: "flex", flexDirection: "column", gap: 12 },
  input: {
    padding: "14px 18px",
    fontSize: 15, fontFamily: "var(--font-sans)",
    background: "#F5F5F7",
    color: "#1A1E1D",
    border: "1px solid rgba(26,30,29,0.12)",
    borderRadius: 999,
    outline: "none",
    transition: "border-color 200ms, background 200ms",
  },
  primaryBtn: {
    marginTop: 4,
    padding: "14px 20px",
    fontSize: 14, fontFamily: "var(--font-sans)", fontWeight: 500,
    letterSpacing: "0.04em",
    background: "#1A1E1D", color: "#FFFFFF",
    border: 0, borderRadius: 999,
    cursor: "pointer",
    transition: "background 200ms",
  },
  secondaryBtn: {
    padding: "12px 20px",
    fontSize: 13, fontFamily: "var(--font-sans)",
    letterSpacing: "0.04em",
    background: "transparent", color: "#1A1E1D",
    border: "1px solid rgba(26,30,29,0.26)",
    borderRadius: 999,
    cursor: "pointer",
  },
  linkBtn: {
    background: "transparent",
    border: 0,
    color: "#21405A",
    fontSize: 12,
    fontFamily: "var(--font-sans)",
    cursor: "pointer",
    padding: 0,
    letterSpacing: "0.04em",
  },
  resendRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginTop: 4,
  },
  note: {
    fontSize: 13, lineHeight: 1.5, color: "rgba(26,30,29,0.66)",
    textAlign: "left", margin: 0,
  },
  err: {
    fontSize: 12, color: "#8A2E1F", textAlign: "left",
    padding: "8px 14px",
    background: "rgba(176,73,44,0.08)",
    border: "1px solid rgba(176,73,44,0.3)",
    borderRadius: 12,
  },
  legal: {
    marginTop: 20,
    fontSize: 11, letterSpacing: "0.02em",
    color: "rgba(26,30,29,0.55)",
  },
  legalLink: { color: "#21405A", textDecoration: "underline" },
  footer: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    fontSize: 11, color: "rgba(26,30,29,0.55)",
    letterSpacing: "0.04em", paddingTop: 24,
    borderTop: "1px solid rgba(26,30,29,0.08)",
  },
  footLink: { color: "inherit", textDecoration: "none" },
};
